import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import CompanyApi from "../api/Company";
import { toast } from "react-hot-toast";
import AddCompanyModal from "../components/modals/AddCompanyModal";
import ChangeBrandModal from "../components/modals/ChangeBrandModal";
import SearchBar from "../components/atoms/SearchBar";
import { useCustomEventListener } from "react-custom-events";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "../components/organisms/Layout";
import PaginationView from "../components/molecules/Pagination";
import useQuery from "../hooks/useQuery";
import { selectUser, selectRoles } from "../store/authSlice";
import Table from "../components/atoms/Tables/Table";
import { addIcon } from "../assets/images";
import debounce from "lodash/debounce";

const Companies = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useQuery();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sorted, setSorted] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const [showChangeBrandModal, setShowChangeBrandModal] = useState(false);
  const [pageLimit, setPagelimit] = useState(query.get("pageSize") || 50);
  const [searchCompany, setSearchCompany] = useState("");
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const user = useSelector(selectUser);
  const roles = useSelector(selectRoles);

  useEffect(() => {
    document.title = "HeyHire Portal-Companies";
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [query]);

  useCustomEventListener("companyAdded", () => {
    setShowModal(false);
    loadCompanies();
  });

  useCustomEventListener("closeModal", () => {
    setShowModal(false);
    loadCompanies();
  });

  useCustomEventListener("closeChangeBrandModal", () => {
    setShowChangeBrandModal(false);
    loadCompanies();
  });

  const debouncedUpdateSearch = useCallback(
    debounce((txt) => {
      // Create a new object from current search params
      const newParams = { ...Object.fromEntries(searchParams) };

      // Reset to page 1 when searching
      newParams.page = 1;

      // Add or remove search parameter based on value
      if (txt && txt.trim() !== '') {
        newParams.search = txt.trim();
      } else {
        delete newParams.search;
      }

      setSearchParams(newParams);
    }, 300),
    [searchParams]
  );

  const searchCompanyName = (txt) => {
    setSearchCompany(txt);
    debouncedUpdateSearch(txt);
  };

  const loadCompanies = () => {
    setLoading(true);
    const page = query.get("page") || 1;
    const sort = query.get("sort") || "";
    const limit = query.get("limit") || pageLimit;
    const search = query.get("search") || "";

    const sortData = sort ? `sort=${sort}&` : "";
    const pageData = page ? `page=${page}` : "";
    const searchData = search ? `&search=${encodeURIComponent(search)}` : "";
    const queryData = sortData + pageData + `&limit=${limit}` + searchData;

    // Update the search input field when loading from URL
    setSearchCompany(search);

    CompanyApi.getAll(queryData)
      .then((response) => {
        setFilteredCompanies(response.data.data);
        setSorted(response.data.data);
        setMetaData(response.data.meta);
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error(t("common.something-went-wrong"));
        }
      })
      .then(() => setLoading(false));
  };

  const handlecPageChange = (currentPage, pageSize) => {
    setPagelimit(pageSize);
    // Create a new object from current search params
    const newParams = { ...Object.fromEntries(searchParams) };

    newParams.page = currentPage;
    newParams.limit = pageSize;

    // Only include search if it has a value
    if (searchCompany && searchCompany.trim() !== '') {
      newParams.search = searchCompany.trim();
    } else {
      delete newParams.search;
    }

    setSearchParams(newParams);
  };


  return (
    <DashboardLayout darmo={true}>
      {showModal && (
        <AddCompanyModal
          company={editCompany}
          title={
            editCompany
              ? t("companies.edit-company")
              : t("companies.add-a-company")
          }
          onClose={() => setShowModal(false)}
          visible={showModal}
        />
      )}

      {showChangeBrandModal && (
        <ChangeBrandModal
          company={editCompany}
          title="Change Brand"
          onClose={() => setShowChangeBrandModal(false)}
          visible={showChangeBrandModal}
        />
      )}

      <div className="pb-12 w-full overflow-x-hidden">
        <Table
          isLoading={loading}
          mainHeader="Companies"
          searchBar={
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full sm:justify-end">
              <div className="w-full sm:w-[500px]">
                <SearchBar
                  searchValue={searchCompany}
                  placeholder="Search by company name or owner..."
                  searchFunction={(value) => searchCompanyName(value)}
                />
              </div>
            </div>
          }
          maxHeight="calc(100vh - 250px)"
          headers={
            !loading && filteredCompanies?.length === 0
              ? ["No data available"]
              : [
                t("common.name"),
                t("common.owner"),
                t("common.number-of-locations"),
                "Actions",
              ]
          }
          data={filteredCompanies?.map((company, index) => (
            <tr
              key={index}
              className="hover:bg-gray-50 transition-colors duration-150"
            >
              <td className="whitespace-nowrap px-3 sm:px-6 py-3 sm:py-4 text-sm">
                <div className="font-semibold text-gray-900">
                  {company?.name}
                </div>
              </td>
              <td className="whitespace-nowrap px-3 sm:px-6 py-3 sm:py-4 text-sm">
                <div className="text-gray-700">
                  {company.owners && company.owners.length > 0
                    ? `${company.owners[0].first_name} ${company.owners[0].last_name}`
                    : "-"}
                </div>
              </td>
              <td className="whitespace-nowrap px-3 sm:px-6 py-3 sm:py-4 text-sm">
                <div className="text-gray-700">
                  {company.locations.length}
                </div>
              </td>
              <td className="whitespace-nowrap px-2 sm:px-3 py-2.5 sm:py-3 text-xs sm:text-sm">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                  <button
                    className="text-primary-600 hover:text-primary-900 font-medium hover:underline whitespace-nowrap"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditCompany(company);
                      setShowModal(true);
                    }}
                  >
                    {t("common.edit")}
                  </button>
                  {roles?.[0] === "super-user" && company?.is_enterprise === 1 && (
                    <button
                      className="text-primary-600 hover:text-primary-900 font-medium hover:underline whitespace-nowrap"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditCompany(company);
                        setShowChangeBrandModal(true);
                      }}
                    >
                      Change Brand
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          buttonData={
            <button
              onClick={() => navigate("/companies/add")}
              className="w-full sm:w-auto flex-shrink-0 cursor-pointer flex justify-center items-center rounded-lg bg-primary-500 px-6 h-10 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 transition-all duration-200"
            >
              <img src={addIcon} alt="add" className="mr-2 w-4 h-4" />
              Add Company
            </button>
          }
        />
        <div className="flex w-full h-20 justify-center md:justify-end items-center px-4">
          {!loading && metaData?.total > 0 && (
            <PaginationView
              current={metaData?.current_page}
              next={metaData?.to}
              pageSize={metaData?.per_page}
              total={metaData?.total}
              onChange={handlecPageChange}
            />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Companies;
